import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiTrendingUp,
  FiTarget, FiArrowRight, FiArrowLeft, FiMapPin, FiClock,
  FiBriefcase, FiStar, FiCheckCircle, FiSend, FiChevronDown,
  FiChevronUp, FiZap, FiGlobe, FiShield, FiLayers,
  FiTerminal, FiBookOpen, FiAward, FiHeart, FiVideo,
  FiPenTool,
} from 'react-icons/fi';

/* ═══════════════════════════════════════════════════════════
   ROLE DATA
   ═══════════════════════════════════════════════════════════ */

const DEPARTMENTS = [
  { key: 'bd', label: 'Business Development', icon: <FiTrendingUp />, color: '#6cb4ff' },
  { key: 'creator', label: 'Creator', icon: <FiVideo />, color: '#a78bfa' },
  { key: 'product-design', label: 'Product Design', icon: <FiPenTool />, color: '#fbbf24' },
  { key: 'research', label: 'Research', icon: <FiBookOpen />, color: '#34d399' },
];

const ROLES = [
  /* ═══════════════════════════════════════════════════════
     BD — Business Development
     ═══════════════════════════════════════════════════════ */
  {
    id: 'bd-lead',
    title: 'Business Development Lead',
    dept: 'bd',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid-Senior',
    salary: '$90K – $140K + commission',
    posted: '2026-02-15',
    summary: 'Find, close, and grow partnerships and client relationships that put Vector Labs products in front of the right people. You\'re the bridge between what we build and who needs it.',
    responsibilities: [
      'Identify and pursue strategic partnerships with companies, accelerators, and organizations',
      'Manage the full sales cycle — outreach, discovery, proposals, close',
      'Build and maintain a pipeline of consulting and product leads',
      'Negotiate contracts, scoping, and pricing for client engagements',
      'Represent Vector Labs at events, conferences, and meetings in the Charlotte tech scene and beyond',
    ],
    requirements: [
      '3+ years in B2B sales, partnerships, or business development in tech',
      'Track record of closing deals and hitting revenue targets',
      'Strong communication skills — written, verbal, and presentation',
      'Understanding of AI/tech products (enough to sell them confidently)',
      'Self-starter who can build the outbound playbook from scratch',
    ],
    niceToHave: [
      'Existing network in the Charlotte or Southeast tech ecosystem',
      'Experience selling consulting or professional services',
      'CRM experience (HubSpot, Salesforce, or similar)',
      'Background in AI, SaaS, or developer tools',
    ],
  },
  {
    id: 'bd-strategist',
    title: 'Partnerships & Strategy Analyst',
    dept: 'bd',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Junior-Mid',
    salary: '$65K – $95K',
    posted: '2026-02-20',
    summary: 'Support business development with market research, competitive analysis, and partnership ops. Help shape where Vector Labs goes next.',
    responsibilities: [
      'Research target markets, competitors, and potential partnership opportunities',
      'Build pitch decks, one-pagers, and proposal materials',
      'Track and manage the deal pipeline in our CRM',
      'Analyze win/loss data and surface insights to improve our approach',
      'Assist with client onboarding and relationship management post-close',
    ],
    requirements: [
      '1+ years in business ops, strategy, consulting, or sales support',
      'Strong research and analytical skills',
      'Excellent writing — you can draft decks and proposals that land',
      'Proficiency with spreadsheets, CRM tools, and presentation software',
      'Hungry to learn — you want a BD career and are ready to hustle for it',
    ],
    niceToHave: [
      'Experience at a startup or consulting firm',
      'Interest in AI, tech products, or the startup ecosystem',
      'Finance, economics, or business analytics background',
      'Data visualization or Notion/Airtable power-user skills',
    ],
  },
  {
    id: 'bd-outreach',
    title: 'Outbound Sales Representative',
    dept: 'bd',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Entry-Mid',
    salary: '$55K – $80K + commission',
    posted: '2026-02-22',
    summary: 'Be the first touch. Run outbound campaigns, book meetings, and build the top of our pipeline so the team can close.',
    responsibilities: [
      'Run multi-channel outreach (cold email, LinkedIn, calls) to target accounts',
      'Book qualified discovery meetings for the BD team',
      'Research prospects and personalize outreach at scale',
      'Manage and optimize sequences in our outreach tooling',
      'Track KPIs — emails sent, response rates, meetings booked, conversion',
    ],
    requirements: [
      'Hunger and grit — you\'re relentless but respectful',
      'Excellent written communication (every email is a first impression)',
      'Organized — you can manage dozens of threads without dropping balls',
      'Comfortable with outreach tools (Apollo, Instantly, Lemlist, or similar)',
      'Coachable — you take feedback and iterate fast',
    ],
    niceToHave: [
      'Previous SDR/BDR experience',
      'Experience selling to technical buyers',
      'Familiarity with AI or SaaS spaces',
      'Personal CRM or sales methodology experience',
    ],
  },

  /* ═══════════════════════════════════════════════════════
     CREATOR — Social Media Influencers
     ═══════════════════════════════════════════════════════ */
  {
    id: 'creator-video',
    title: 'Video Content Creator',
    dept: 'creator',
    type: 'Full-time / Contract',
    location: 'Charlotte, NC / Remote',
    level: 'Mid',
    salary: '$70K – $110K',
    posted: '2026-02-18',
    summary: 'Create short-form and long-form video content that makes AI, startups, and tech culture accessible and exciting. You\'ll be the face (or voice) of Vector Labs on social.',
    responsibilities: [
      'Plan, shoot, and edit video content for YouTube, TikTok, Instagram, and LinkedIn',
      'Break down complex AI and tech concepts into engaging, shareable content',
      'Develop recurring series and content formats that build an audience',
      'Collaborate with research & product teams to create behind-the-scenes and demo content',
      'Track analytics, optimize for growth, and iterate based on what resonates',
    ],
    requirements: [
      '2+ years creating video content (personal brand or professional)',
      'Portfolio showing strong storytelling and editing skills',
      'Comfortable on camera or behind it — or both',
      'Understanding of platform algorithms and content strategy (YT, TikTok, IG)',
      'Self-directed — you can concept, shoot, and post without a production team',
    ],
    niceToHave: [
      'Existing audience or following in tech/startup space',
      'Experience with After Effects, Premiere Pro, or DaVinci Resolve',
      'Interest in AI, machine learning, or developer culture',
      'Podcast or livestream experience',
    ],
  },
  {
    id: 'creator-social',
    title: 'Social Media Manager',
    dept: 'creator',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid',
    salary: '$60K – $90K',
    posted: '2026-02-21',
    summary: 'Own Vector Labs\' social media presence across all platforms. Build community, drive engagement, and make people care about what we\'re building.',
    responsibilities: [
      'Manage daily posting across Twitter/X, LinkedIn, Instagram, TikTok, and YouTube',
      'Develop and maintain a content calendar aligned with product launches and research drops',
      'Engage with the community — reply, retweet, build relationships in the AI/tech ecosystem',
      'Write sharp, on-brand copy that balances technical credibility with approachability',
      'Track growth metrics, engagement rates, and funnel impact from social',
    ],
    requirements: [
      '2+ years managing social media for a brand or personal account with real traction',
      'Strong copywriting — you can write threads that go viral and captions that convert',
      'Deep understanding of social platform mechanics and trends',
      'Design eye — comfortable using Canva, Figma, or similar for quick assets',
      'Data-driven — you track what works and double down',
    ],
    niceToHave: [
      'Experience in the tech, AI, or startup space',
      'Community building or Discord/Slack management experience',
      'Basic video editing for social clips and reels',
      'Personal brand with demonstrated reach',
    ],
  },
  {
    id: 'creator-writer',
    title: 'Content Writer & Storyteller',
    dept: 'creator',
    type: 'Full-time / Contract',
    location: 'Charlotte, NC / Remote',
    level: 'Mid',
    salary: '$65K – $100K',
    posted: '2026-02-24',
    summary: 'Write the words that define Vector Labs. Blog posts, case studies, landing pages, newsletters, and tweets — you turn our work into stories people want to read.',
    responsibilities: [
      'Write blog posts, case studies, and long-form content for the engineering log',
      'Craft product copy, landing pages, and email campaigns',
      'Develop Vector Labs\' brand voice — technical but human, ambitious but honest',
      'Turn research papers and internal work into accessible, engaging narratives',
      'Write social copy and support the creator team with captions and scripts',
    ],
    requirements: [
      '2+ years professional writing (content marketing, journalism, or technical writing)',
      'Portfolio showing range — long-form, short-form, technical, and persuasive',
      'Ability to interview engineers and researchers and turn conversations into content',
      'Strong editing skills — you tighten your own work before anyone else sees it',
      'Genuine curiosity about technology and startups',
    ],
    niceToHave: [
      'Technical background — CS coursework, coding, or dev experience',
      'SEO knowledge and keyword research experience',
      'Newsletter writing experience (Substack, Beehiiv, ConvertKit)',
      'Experience writing about AI, machine learning, or data',
    ],
  },

  /* ═══════════════════════════════════════════════════════
     PRODUCT DESIGN — Design and Engineering
     ═══════════════════════════════════════════════════════ */
  {
    id: 'pd-designer',
    title: 'Product Designer',
    dept: 'product-design',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid-Senior',
    salary: '$95K – $140K',
    posted: '2026-02-12',
    summary: 'Design the interfaces and experiences for AI-powered products used by real people. Go from user research to shipped pixels and own every detail.',
    responsibilities: [
      'Own end-to-end design for product features — research, wireframes, prototypes, final UI',
      'Create and maintain our design system and component library',
      'Conduct user research and usability testing to validate design decisions',
      'Collaborate daily with engineers on implementation — sweat the details',
      'Design data-heavy interfaces (dashboards, charts, editors) that feel intuitive and fast',
    ],
    requirements: [
      '3+ years product design experience with shipped work',
      'Proficiency in Figma (or equivalent)',
      'Strong portfolio showing end-to-end product design for web apps',
      'Experience designing data-heavy or technical interfaces',
      'Understanding of front-end constraints — CSS, responsive design, component architecture',
    ],
    niceToHave: [
      'Can write HTML/CSS/React (design-engineer hybrid)',
      'Experience designing developer tools, B2B products, or AI interfaces',
      'Motion design skills (Framer Motion, Lottie, CSS animations)',
      'Design systems or design tokens experience',
    ],
  },
  {
    id: 'pd-fullstack',
    title: 'Full-Stack Engineer',
    dept: 'product-design',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid-Senior',
    salary: '$100K – $150K',
    posted: '2026-02-15',
    summary: 'Build and ship product features across our entire stack — React frontends, Node/Python backends, and cloud infrastructure. You own everything from prototype to production.',
    responsibilities: [
      'Design and implement product features end-to-end, from database to UI',
      'Write clean, tested, maintainable code across React, Node.js, and Python',
      'Collaborate directly with designers and the founder on architecture decisions',
      'Ship fast — we deploy multiple times per day',
      'Review PRs, improve dev tooling, and set engineering standards for the team',
    ],
    requirements: [
      '3+ years building production web applications',
      'Strong JavaScript/TypeScript — React, Node.js, Express',
      'Experience with databases (PostgreSQL, MongoDB, or similar)',
      'Comfortable with cloud platforms (AWS, GCP, or Azure)',
      'Self-directed — you identify what needs to be built and do it',
    ],
    niceToHave: [
      'Experience integrating AI/ML (LLM APIs, embeddings, RAG pipelines)',
      'DevOps / CI-CD experience',
      'Open source contributions',
      'Early-stage startup experience',
    ],
  },
  {
    id: 'pd-frontend',
    title: 'Frontend Engineer',
    dept: 'product-design',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid',
    salary: '$90K – $130K',
    posted: '2026-02-20',
    summary: 'Own the UI across all Vector Labs products. Build fast, accessible, delightful interfaces that make complex AI tools feel simple.',
    responsibilities: [
      'Build responsive, accessible UIs with React and modern CSS',
      'Implement data visualizations, interactive charts, and real-time dashboards',
      'Optimize performance — bundle size, rendering, Core Web Vitals',
      'Work closely with product design to translate mockups into pixel-perfect interfaces',
      'Write component tests and maintain the frontend architecture',
    ],
    requirements: [
      '2+ years professional React experience',
      'Deep understanding of CSS, responsive design, and accessibility (WCAG)',
      'Experience with state management (hooks, context, or similar)',
      'Eye for design — you notice when something is 2px off',
      'Familiarity with REST APIs and async data patterns',
    ],
    niceToHave: [
      'D3.js, Recharts, or data visualization experience',
      'Animation / motion design skills (Framer Motion, CSS transitions)',
      'TypeScript experience',
      'Design skills — Figma, component libraries',
    ],
  },
  {
    id: 'pd-backend',
    title: 'Backend / Infrastructure Engineer',
    dept: 'product-design',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Senior',
    salary: '$120K – $160K',
    posted: '2026-02-10',
    summary: 'Build the APIs, data pipelines, and infrastructure that power our products. Design systems that handle real-time data at scale and never go down.',
    responsibilities: [
      'Architect and build scalable REST/GraphQL APIs',
      'Design database schemas and optimize query performance',
      'Build CI/CD pipelines and manage cloud infrastructure (AWS)',
      'Implement monitoring, alerting, and incident response tooling',
      'Ensure security best practices across all backend services',
    ],
    requirements: [
      '4+ years backend engineering (Node.js, Python, Go, or Rust)',
      'Strong database skills — PostgreSQL, Redis, DynamoDB',
      'AWS experience (EC2, Lambda, S3, RDS, or equivalent)',
      'Understanding of distributed systems and microservice patterns',
      'Security-first mindset',
    ],
    niceToHave: [
      'Kubernetes / Docker orchestration experience',
      'Message queues (SQS, Kafka, RabbitMQ)',
      'Terraform or CloudFormation IaC',
      'ML model serving or inference infrastructure experience',
    ],
  },

  /* ═══════════════════════════════════════════════════════
     RESEARCH — Curate Research
     ═══════════════════════════════════════════════════════ */
  {
    id: 'research-scientist',
    title: 'AI Research Scientist',
    dept: 'research',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Senior',
    salary: '$130K – $180K',
    posted: '2026-02-12',
    summary: 'Push the boundaries of applied AI. Conduct original research in multi-agent systems, automation, and developer tooling — then help ship it into actual products.',
    responsibilities: [
      'Conduct applied research in AI agent orchestration, automation, and LLM systems',
      'Write and publish technical papers in the Vector Labs research catalogue',
      'Prototype new approaches and validate them with real-world data and benchmarks',
      'Collaborate with the product design team to move research into shipped products',
      'Stay current with state-of-the-art and identify opportunities for the lab',
    ],
    requirements: [
      'MS or PhD in Computer Science, AI/ML, or related field',
      'Track record of published research (conferences or journals)',
      'Strong implementation skills — you can code your own ideas in Python/PyTorch',
      'Experience with transformer architectures, attention mechanisms, and modern LLMs',
      'Excellent technical writing skills',
    ],
    niceToHave: [
      'Experience with reinforcement learning or multi-agent RL',
      'Background in NLP, information retrieval, or knowledge graphs',
      'Prior industry research lab experience (Google Brain, FAIR, DeepMind, etc.)',
      'Open source ML contributions',
    ],
  },
  {
    id: 'research-ml-eng',
    title: 'Machine Learning Engineer',
    dept: 'research',
    type: 'Full-time',
    location: 'Charlotte, NC / Remote',
    level: 'Mid-Senior',
    salary: '$110K – $160K',
    posted: '2026-02-18',
    summary: 'Bridge the gap between research and production. Take ML models from prototype notebooks to deployed, monitored systems serving real users.',
    responsibilities: [
      'Design and implement ML pipelines — training, evaluation, deployment',
      'Fine-tune and optimize LLMs for domain-specific tasks',
      'Build RAG systems, embedding pipelines, and vector search infrastructure',
      'Collaborate with researchers to productionize experimental results',
      'Monitor model performance, run evaluations, and implement A/B testing frameworks',
    ],
    requirements: [
      '3+ years ML engineering in production environments',
      'Strong Python — PyTorch, HuggingFace, or TensorFlow',
      'Experience with LLM APIs (OpenAI, Anthropic, open-source models)',
      'Understanding of vectors, embeddings, and semantic search',
      'Familiarity with MLOps tools (MLflow, Weights & Biases, etc.)',
    ],
    niceToHave: [
      'Published research or conference papers',
      'Experience with multi-agent systems or agentic frameworks',
      'Rust or C++ for performance-critical inference',
      'Experience scaling ML systems to production traffic',
    ],
  },
  {
    id: 'research-curator',
    title: 'Research Curator & Technical Writer',
    dept: 'research',
    type: 'Full-time / Contract',
    location: 'Charlotte, NC / Remote',
    level: 'Mid',
    salary: '$70K – $100K',
    posted: '2026-02-25',
    summary: 'Discover, evaluate, and curate the best AI research for Vector Labs\' catalogue. Turn dense papers into accessible summaries and keep our research library world-class.',
    responsibilities: [
      'Monitor arXiv, conference proceedings, and top labs for relevant new research',
      'Read, evaluate, and summarize papers — identify what matters for our work',
      'Maintain and organize the Vector Labs research catalogue with tags, categories, and summaries',
      'Write research digests and roundups for the team and public audience',
      'Collaborate with researchers to edit and polish papers before publication',
    ],
    requirements: [
      'Strong technical reading skills — you can parse ML papers and extract key insights',
      'Excellent writing — clear, concise, accurate technical summaries',
      'Background in CS, AI/ML, data science, or adjacent technical field',
      'Organized and systematic — you can manage a growing library of hundreds of papers',
      'Genuine passion for keeping up with the AI research landscape',
    ],
    niceToHave: [
      'Graduate-level AI/ML coursework or research experience',
      'Experience with Zotero, Semantic Scholar, or reference management tools',
      'Previous technical writing, journalism, or science communication experience',
      'Familiarity with LaTeX and academic publishing workflows',
    ],
  },
];

const VALUES = [
  { icon: <FiZap />, title: 'Ship Fast', desc: 'We measure progress in days, not quarters. If it\'s not deployed, it doesn\'t count.' },
  { icon: <FiTarget />, title: 'Own the Outcome', desc: 'No handoffs. You own your work from idea to production to metrics.' },
  { icon: <FiBookOpen />, title: 'Learn in Public', desc: 'We publish our research, write about our mistakes, and share what we learn.' },
  { icon: <FiShield />, title: 'No BS', desc: 'We say what we mean. No corporate speak, no vanity metrics, no theater.' },
  { icon: <FiHeart />, title: 'People First', desc: 'Sustainable pace. Mental health matters. We build a team, not a burnout factory.' },
  { icon: <FiGlobe />, title: 'Remote-Friendly', desc: 'HQ in Charlotte. Work from anywhere. Results matter, not your location.' },
];

const BENEFITS = [
  'Competitive salary + equity',
  'Health, dental, and vision insurance',
  'Unlimited PTO (and we actually use it)',
  'Home office stipend ($1,500)',
  'Learning budget ($2,000/year)',
  'Conference attendance',
  'Flexible hours — async-first culture',
  'Latest hardware (MacBook Pro or equivalent)',
];

/* ═══════════════════════════════════════════════════════════
   ONBOARDING / APPLICATION FORM
   ═══════════════════════════════════════════════════════════ */

function ApplicationForm({ role, onBack }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', linkedin: '', github: '', portfolio: '',
    experience: '', currentRole: '', location: '',
    whyVector: '', relevantWork: '', availability: '',
    resume: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const steps = [
    {
      title: 'About You',
      subtitle: 'Basic information so we can get in touch.',
      fields: (
        <>
          <div className="career-form-row">
            <div className="career-form-field">
              <label>Full Name *</label>
              <input type="text" value={form.name} onChange={set('name')} placeholder="Jane Doe" required />
            </div>
            <div className="career-form-field">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" required />
            </div>
          </div>
          <div className="career-form-row">
            <div className="career-form-field">
              <label>Phone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 (555) 123-4567" />
            </div>
            <div className="career-form-field">
              <label>Location</label>
              <input type="text" value={form.location} onChange={set('location')} placeholder="Charlotte, NC" />
            </div>
          </div>
          <div className="career-form-row">
            <div className="career-form-field">
              <label>LinkedIn</label>
              <input type="url" value={form.linkedin} onChange={set('linkedin')} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="career-form-field">
              <label>GitHub / Portfolio</label>
              <input type="url" value={form.github} onChange={set('github')} placeholder="https://github.com/..." />
            </div>
          </div>
        </>
      ),
      valid: form.name.trim() && form.email.trim(),
    },
    {
      title: 'Experience',
      subtitle: 'Tell us about your background.',
      fields: (
        <>
          <div className="career-form-row">
            <div className="career-form-field">
              <label>Current / Most Recent Role</label>
              <input type="text" value={form.currentRole} onChange={set('currentRole')} placeholder="Senior Engineer at Acme Corp" />
            </div>
            <div className="career-form-field">
              <label>Years of Relevant Experience</label>
              <select value={form.experience} onChange={set('experience')}>
                <option value="">Select...</option>
                <option value="0-1">0–1 years</option>
                <option value="1-3">1–3 years</option>
                <option value="3-5">3–5 years</option>
                <option value="5-8">5–8 years</option>
                <option value="8+">8+ years</option>
              </select>
            </div>
          </div>
          <div className="career-form-field full">
            <label>Resume / CV</label>
            <div className="career-file-upload">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setForm({ ...form, resume: e.target.files?.[0] || null })}
              />
              <span>{form.resume ? form.resume.name : 'Drop your resume or click to browse (PDF, DOC)'}</span>
            </div>
          </div>
          <div className="career-form-field full">
            <label>Availability</label>
            <select value={form.availability} onChange={set('availability')}>
              <option value="">When can you start?</option>
              <option value="immediately">Immediately</option>
              <option value="2-weeks">2 weeks notice</option>
              <option value="1-month">1 month</option>
              <option value="2-months">2+ months</option>
            </select>
          </div>
        </>
      ),
      valid: true,
    },
    {
      title: 'Why Vector Labs?',
      subtitle: 'We read every answer. Be yourself.',
      fields: (
        <>
          <div className="career-form-field full">
            <label>Why do you want to work at Vector Labs? *</label>
            <textarea
              value={form.whyVector}
              onChange={set('whyVector')}
              placeholder="What excites you about what we're building? What problems do you want to solve?"
              rows={5}
            />
          </div>
          <div className="career-form-field full">
            <label>Describe your most relevant work or project</label>
            <textarea
              value={form.relevantWork}
              onChange={set('relevantWork')}
              placeholder="Link a project, describe a feature you shipped, or tell us about research you've done. Specifics beat buzzwords."
              rows={5}
            />
          </div>
        </>
      ),
      valid: form.whyVector.trim().length > 0,
    },
  ];

  const handleSubmit = () => {
    // In production, POST to an API. For now, log + show confirmation.
    console.log('Application submitted:', { role: role.title, ...form });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="career-app-page">
        <div className="career-app-success">
          <FiCheckCircle size={48} />
          <h2>Application Submitted!</h2>
          <p>Thanks for applying for <strong>{role.title}</strong> at Vector Labs.</p>
          <p>We review every application personally. You'll hear from us within 5 business days.</p>
          <button className="career-btn primary" onClick={onBack}>
            <FiArrowLeft /> Back to All Roles
          </button>
        </div>
      </div>
    );
  }

  const current = steps[step];

  return (
    <div className="career-app-page">
      <button className="career-back-link" onClick={onBack}>
        <FiArrowLeft /> Back to {role.title}
      </button>

      <div className="career-app-header">
        <h2>Apply: {role.title}</h2>
        <span className="career-app-dept">{DEPARTMENTS.find(d => d.key === role.dept)?.label}</span>
      </div>

      {/* Progress bar */}
      <div className="career-progress">
        {steps.map((s, i) => (
          <div
            key={i}
            className={`career-progress-step ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}
            onClick={() => i < step && setStep(i)}
          >
            <span className="career-progress-dot">{i < step ? <FiCheckCircle /> : i + 1}</span>
            <span className="career-progress-label">{s.title}</span>
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="career-step-content">
        <h3>{current.title}</h3>
        <p className="career-step-sub">{current.subtitle}</p>
        {current.fields}
      </div>

      {/* Navigation */}
      <div className="career-step-nav">
        {step > 0 && (
          <button className="career-btn outline" onClick={() => setStep(step - 1)}>
            <FiArrowLeft /> Previous
          </button>
        )}
        <div style={{ flex: 1 }} />
        {step < steps.length - 1 ? (
          <button
            className="career-btn primary"
            disabled={!current.valid}
            onClick={() => setStep(step + 1)}
          >
            Next <FiArrowRight />
          </button>
        ) : (
          <button
            className="career-btn primary submit"
            disabled={!current.valid}
            onClick={handleSubmit}
          >
            <FiSend /> Submit Application
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ROLE DETAIL VIEW
   ═══════════════════════════════════════════════════════════ */

function RoleDetail({ role, onBack, onApply }) {
  const dept = DEPARTMENTS.find(d => d.key === role.dept);
  const [expandedSection, setExpandedSection] = useState('responsibilities');

  const toggle = (section) => setExpandedSection(expandedSection === section ? null : section);

  return (
    <div className="career-detail">
      <button className="career-back-link" onClick={onBack}>
        <FiArrowLeft /> All Open Roles
      </button>

      <div className="career-detail-hero">
        <div className="career-detail-badge" style={{ color: dept?.color }}>
          {dept?.icon} {dept?.label}
        </div>
        <h1>{role.title}</h1>
        <div className="career-detail-meta">
          <span><FiMapPin /> {role.location}</span>
          <span><FiClock /> {role.type}</span>
          <span><FiBriefcase /> {role.level}</span>
          <span><FiStar /> {role.salary}</span>
        </div>
        <p className="career-detail-summary">{role.summary}</p>
        <button className="career-btn primary large" onClick={onApply}>
          Apply Now <FiArrowRight />
        </button>
      </div>

      {/* Expandable sections */}
      <div className="career-detail-sections">
        {[
          { key: 'responsibilities', title: 'What You\'ll Do', items: role.responsibilities, icon: <FiTarget /> },
          { key: 'requirements', title: 'What We\'re Looking For', items: role.requirements, icon: <FiCheckCircle /> },
          { key: 'niceToHave', title: 'Nice to Have', items: role.niceToHave, icon: <FiAward /> },
        ].map((section) => (
          <div key={section.key} className={`career-accordion ${expandedSection === section.key ? 'open' : ''}`}>
            <button className="career-accordion-header" onClick={() => toggle(section.key)}>
              <span>{section.icon} {section.title}</span>
              {expandedSection === section.key ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSection === section.key && (
              <ul className="career-accordion-body">
                {section.items.map((item, i) => (
                  <li key={i}><FiArrowRight className="career-bullet-icon" /> {item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="career-detail-bottom-cta">
        <h3>Sound like you?</h3>
        <p>We'd love to hear from you — even if you don't check every box.</p>
        <button className="career-btn primary large" onClick={onApply}>
          Apply for This Role <FiArrowRight />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN CAREERS PAGE
   ═══════════════════════════════════════════════════════════ */

export default function CareersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDept = searchParams.get('dept') || 'all';
  const selectedRoleId = searchParams.get('role');
  const applying = searchParams.get('apply') === 'true';

  const setDept = (dept) => {
    const params = new URLSearchParams();
    if (dept !== 'all') params.set('dept', dept);
    setSearchParams(params);
  };

  const selectRole = (roleId) => {
    const params = new URLSearchParams(searchParams);
    params.set('role', roleId);
    params.delete('apply');
    setSearchParams(params);
  };

  const startApply = () => {
    const params = new URLSearchParams(searchParams);
    params.set('apply', 'true');
    setSearchParams(params);
  };

  const goBack = () => {
    const params = new URLSearchParams();
    if (selectedDept !== 'all') params.set('dept', selectedDept);
    setSearchParams(params);
  };

  const filteredRoles = useMemo(() => {
    if (selectedDept === 'all') return ROLES;
    return ROLES.filter(r => r.dept === selectedDept);
  }, [selectedDept]);

  const selectedRole = ROLES.find(r => r.id === selectedRoleId);

  /* ── Applying state ── */
  if (applying && selectedRole) {
    return (
      <div className="careers-page">
        <div className="careers-container">
          <ApplicationForm role={selectedRole} onBack={goBack} />
        </div>
      </div>
    );
  }

  /* ── Role detail state ── */
  if (selectedRole) {
    return (
      <div className="careers-page">
        <div className="careers-container">
          <RoleDetail role={selectedRole} onBack={goBack} onApply={startApply} />
        </div>
      </div>
    );
  }

  /* ── Main listing ── */
  return (
    <div className="careers-page">
      {/* Hero */}
      <section className="careers-hero">
        <div className="careers-hero-inner">
          <div className="careers-hero-badge"><FiTerminal /> We're hiring</div>
          <h1>Build the Future<br />of Applied AI</h1>
          <p className="careers-hero-sub">
            Vector Labs is assembling a small, elite team to ship AI products that
            solve real problems. We move fast, build in public, and care about craft.
            If you want to work on hard problems with smart people — keep reading.
          </p>
          <div className="careers-hero-stats">
            <div className="careers-stat">
              <strong>{ROLES.length}</strong>
              <span>Open Roles</span>
            </div>
            <div className="careers-stat">
              <strong>{DEPARTMENTS.length}</strong>
              <span>Teams</span>
            </div>
            <div className="careers-stat">
              <strong>100%</strong>
              <span>Remote-Friendly</span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="careers-values">
        <div className="careers-container">
          <h2 className="careers-section-title">How We Work</h2>
          <div className="careers-values-grid">
            {VALUES.map((v, i) => (
              <div key={i} className="careers-value-card">
                <div className="careers-value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="careers-benefits">
        <div className="careers-container">
          <h2 className="careers-section-title">What We Offer</h2>
          <div className="careers-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className="careers-benefit">
                <FiCheckCircle /> {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="careers-roles" id="roles">
        <div className="careers-container">
          <h2 className="careers-section-title">Open Roles</h2>

          {/* Department filter pills */}
          <div className="careers-dept-filters">
            <button
              className={`careers-dept-pill ${selectedDept === 'all' ? 'active' : ''}`}
              onClick={() => setDept('all')}
            >
              <FiLayers /> All ({ROLES.length})
            </button>
            {DEPARTMENTS.map((dept) => {
              const count = ROLES.filter(r => r.dept === dept.key).length;
              return (
                <button
                  key={dept.key}
                  className={`careers-dept-pill ${selectedDept === dept.key ? 'active' : ''}`}
                  style={selectedDept === dept.key ? { borderColor: dept.color, color: dept.color } : {}}
                  onClick={() => setDept(dept.key)}
                >
                  {dept.icon} {dept.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Role cards */}
          <div className="careers-role-list">
            {filteredRoles.map((role) => {
              const dept = DEPARTMENTS.find(d => d.key === role.dept);
              return (
                <button
                  key={role.id}
                  className="careers-role-card"
                  onClick={() => selectRole(role.id)}
                >
                  <div className="careers-role-card-top">
                    <span className="careers-role-dept" style={{ color: dept?.color }}>
                      {dept?.icon} {dept?.label}
                    </span>
                    <span className="careers-role-type">{role.type}</span>
                  </div>
                  <h3 className="careers-role-title">{role.title}</h3>
                  <p className="careers-role-summary">{role.summary}</p>
                  <div className="careers-role-meta">
                    <span><FiMapPin /> {role.location}</span>
                    <span><FiBriefcase /> {role.level}</span>
                    <span><FiStar /> {role.salary}</span>
                  </div>
                  <div className="careers-role-cta">
                    View Role <FiArrowRight />
                  </div>
                </button>
              );
            })}
          </div>

          {filteredRoles.length === 0 && (
            <div className="careers-empty">
              <p>No open roles in this department right now. Check back soon or apply to a different team!</p>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="careers-bottom-cta">
        <div className="careers-container">
          <h2>Don't See Your Role?</h2>
          <p>
            We're always looking for exceptional people. If you think you'd be a
            great fit but don't see the perfect role — reach out anyway.
          </p>
          <a href="mailto:careers@vectorlabsnc.com" className="career-btn primary large">
            <FiSend /> Send Us a Note
          </a>
        </div>
      </section>
    </div>
  );
}
